import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const Table = ({ 
  columns, 
  data, 
  onEdit, 
  onDelete,
  searchable = false,
  defaultSort = { field: null, direction: 'asc' },
  itemsPerPage = 25
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(defaultSort);

  // Sorting logic
  const sortedData = React.useMemo(() => {
    if (!sortConfig.field) return data;
    
    return [...data].sort((a, b) => {
      if (a[sortConfig.field] < b[sortConfig.field]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.field] > b[sortConfig.field]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <View style={styles.container}>
      <ScrollView 
        horizontal={false}
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View style={styles.tableWrapper}>
          {/* Header */}
          <View style={styles.header}>
            {columns.map((column, colIndex) => (
              <TouchableOpacity
                key={column.field || `header-${colIndex}`}
                onPress={() => column.sortable !== false && handleSort(column.field)}
                style={[
                  styles.headerCell,
                  column.sortable !== false && styles.sortableCell,
                  column.width && { width: column.width, minWidth: column.width, flex: 0 },
                  column.align === 'right' && { alignItems: 'flex-end' },
                  column.align === 'center' && { alignItems: 'center' }
                ]}
              >
                <View style={styles.headerContent}>
                  <Text style={styles.headerText}>{column.header}</Text>
                  {sortConfig.field === column.field && (
                    <Text style={styles.sortIcon}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            ))}
            {(onEdit || onDelete) && (
              <View style={[styles.headerCell, styles.actionCell, { borderBottomWidth: 0 }]}>
                <Text style={styles.headerText}>Actions</Text>
              </View>
            )}
          </View>

          {/* Body */}
          <View style={styles.body}>
            {paginatedData.map((item, index) => (
              <View key={item.id || index} style={styles.row}>
            {columns.map((column, colIndex) => {
              
              return (
                <View 
                  key={`${item.id || index}-${column.field || colIndex}`} 
                  style={[
                    styles.cell,
                    column.width && { width: column.width, minWidth: column.width, flex: 0 },
                    column.align === 'right' && { alignItems: 'flex-end' },
                    column.align === 'center' && { alignItems: 'center' }
                  ]}
                >
                  {column.render ? column.render(item[column.field], item) : (
                    <Text 
                      style={[
                        styles.cellText,
                        column.align === 'right' && { textAlign: 'right' },
                        column.align === 'center' && { textAlign: 'center' }
                      ]}
                    >
                      {item[column.field]}
                    </Text>
                  )}
                </View>
              );
            })}
            {(onEdit || onDelete) && (
              <View style={[styles.cell, styles.actionCell]}>
                <View style={styles.actionButtons}>
                  {onEdit && (
                    <TouchableOpacity
                      onPress={() => onEdit(item)}
                      style={[styles.actionButton, styles.editButton]}
                    >
                      <Text style={styles.editButtonText}>Edit</Text>
                    </TouchableOpacity>
                  )}
                  {onDelete && (
                    <TouchableOpacity
                      onPress={() => onDelete(item)}
                      style={[styles.actionButton, styles.deleteButton]}
                    >
                      <Text style={styles.deleteButtonText}>Delete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            )}
              </View>

            ))}
          </View>
        </View>
      </ScrollView>

      {/* Pagination */}
      {totalPages > 1 && (
        <View style={styles.pagination}>
          <Text style={styles.paginationText}>
            {`Showing ${((currentPage - 1) * itemsPerPage) + 1} to ${Math.min(currentPage * itemsPerPage, sortedData.length)} of ${sortedData.length}`}
          </Text>
          <View style={styles.paginationButtons}>
            {currentPage > 1 && (
              <TouchableOpacity
                key="pagination-prev"
                onPress={() => setCurrentPage(p => Math.max(1, p - 1))}
                style={styles.paginationButton}
              >
                <Ionicons 
                  name="chevron-back" 
                  size={16} 
                  color="#4B5563" 
                />
              </TouchableOpacity>
            )}
            {currentPage < totalPages && (
              <TouchableOpacity
                key="pagination-next"
                onPress={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                style={styles.paginationButton}
              >
                <Ionicons 
                  name="chevron-forward" 
                  size={16} 
                  color="#4B5563" 
                />
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 6,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scrollViewContent: {
    flexGrow: 1,
    width: '100%',
  },
  tableWrapper: {
    flex: Platform.OS === 'web' ? 1 : 0,
    width: '100%',
    minWidth: '100%',
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
  },
  header: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerCell: {
    padding: 4,
    paddingVertical: 8,
    minWidth: 100,
    flex: 1,
    justifyContent: 'flex-start',
  },
  sortableCell: {
    cursor: 'pointer',
    ...(Platform.OS === 'web' ? {
      transition: 'background-color 0.2s ease',
      ':hover': {
        backgroundColor: '#F3F4F6'
      }
    } : {}),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  headerText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  sortIcon: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
  },
  body: {
    backgroundColor: '#FFFFFF',
    maxHeight: Platform.OS === 'web' ? 'calc(100vh - 400px)' : '100%',
    overflow: Platform.OS === 'web' ? 'auto' : 'scroll',
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    ...(Platform.OS === 'web' ? {
      transition: 'background-color 0.2s ease',
      ':hover': {
        backgroundColor: '#F9FAFB'
      }
    } : {})
  },
  cell: {
    padding: 4,
    paddingVertical: 2,
    minWidth: Platform.OS === 'web' ? 120 : 100,
    flex: 1,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 13,
    color: '#111827',
  },
  actionCell: {
    width: 120,
    alignItems: 'flex-end',
    flex: 0,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  actionButton: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    minWidth: 50,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    } : {}),
  },
  editButton: {
    backgroundColor: '#EFF6FF',
    ...(Platform.OS === 'web' ? {
      ':hover': {
        backgroundColor: '#DBEAFE',
      }
    } : {}),
  },
  deleteButton: {
    backgroundColor: '#FEF2F2',
    ...(Platform.OS === 'web' ? {
      ':hover': {
        backgroundColor: '#FEE2E2',
      }
    } : {}),
  },
  editButtonText: {
    color: '#2563EB',
    fontSize: 12,
    fontWeight: '500',
  },
  deleteButtonText: {
    color: '#DC2626',
    fontSize: 12,
    fontWeight: '500',
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  paginationText: {
    fontSize: 12,
    color: '#6B7280',
  },
  paginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  paginationButton: {
    padding: 6,
    borderRadius: 4,
    backgroundColor: '#F3F4F6',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      ':hover': {
        backgroundColor: '#E5E7EB',
      }
    } : {}),
  },
  paginationButtonDisabled: {
    backgroundColor: '#F9FAFB',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
});

export default Table;
